import axios from 'axios';

// Создаем экземпляр axios с базовой конфигурацией
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || (() => {
    const hostname = window.location.hostname;
    if (hostname === 'localhost') {
      return 'http://localhost:8000';
    } else if (hostname === 'ggdesk.duckdns.org') {
      // Используем относительный путь для продакшена
      return '';
    }
    return '';
  })(),
  timeout: 30000,
});



// Интерцептор для добавления токена к запросам
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Флаг для предотвращения множественных попыток обновления токена
let isRefreshing = false;
let refreshAttempts = 0;
const maxRefreshAttempts = 3;
let lastRefreshAttemptTime = 0;
const refreshCooldownTime = 5 * 60 * 1000; // 5 минут в миллисекундах
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (error?: any) => void;
}> = [];

// Функция для сброса счетчика попыток если прошло достаточно времени
const resetRefreshAttemptsIfNeeded = () => {
  const now = Date.now();
  if (now - lastRefreshAttemptTime > refreshCooldownTime) {
    refreshAttempts = 0;
    console.log('🔄 Сброшен счетчик попыток refresh токена по времени');
  }
};

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  
  failedQueue = [];
};

// Интерцептор для обработки ответов
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Логин/регистрация: 401 здесь = неверные данные, НЕ повод обновлять токен.
    const _url: string = originalRequest?.url || '';
    const _isAuthAttempt = _url.includes('/api/auth/login') || _url.includes('/api/auth/register');

    // Проверяем, что это 401 ошибка и не refresh/login/register запрос
    if (error.response?.status === 401 && !originalRequest._retry && !_url.includes('/api/auth/refresh') && !_isAuthAttempt) {
      // Сбрасываем счетчик если прошло достаточно времени
      resetRefreshAttemptsIfNeeded();
      
      // Проверяем, не превышено ли количество попыток refresh
      if (refreshAttempts >= maxRefreshAttempts) {
        console.error('❌ Превышено максимальное количество попыток обновления токена');
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        delete api.defaults.headers.common['Authorization'];
        refreshAttempts = 0; // Сбрасываем счетчик
        if (!['/login', '/register'].includes(window.location.pathname)) {
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
      
      if (isRefreshing) {
        // Если уже идет процесс обновления токена, добавляем запрос в очередь
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;
      refreshAttempts++;
      lastRefreshAttemptTime = Date.now(); // Записываем время попытки

      try {
        console.log('🔄 Попытка обновления токена...');
        
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          // Нет refresh-токена — тихо очищаем сессию и уводим на логин,
          // без пугающего тоста и без зацикливания запросов.
          localStorage.removeItem('token');
          delete api.defaults.headers.common['Authorization'];
          if (!['/login', '/register'].includes(window.location.pathname)) {
            window.location.href = '/login';
          }
          return Promise.reject(error);
        }

        const response = await api.post('/api/auth/refresh', { refresh: refreshToken });
        
        const { token } = response.data?.data || response.data;
        
        if (!token) {
          throw new Error('Не получен новый токен');
        }
        
        console.log('✅ Токен успешно обновлен');
        localStorage.setItem('token', token);
        originalRequest.headers.Authorization = `Bearer ${token}`;
        
        // Сбрасываем счетчик попыток при успешном refresh
        refreshAttempts = 0;
        
        processQueue(null, token);
        
        return api(originalRequest);
      } catch (refreshError) {
        console.error('❌ Ошибка обновления токена:', refreshError);
        
        processQueue(refreshError, null);
        
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        delete api.defaults.headers.common['Authorization'];
        
        // Перенаправляем на страницу входа только один раз
        if (!['/login', '/register'].includes(window.location.pathname)) {
          console.log('🔄 Перенаправление на страницу входа...');
          window.location.href = '/login';
        }
        
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
        // Не сбрасываем refreshAttempts здесь, только при успешном refresh
      }
    }

    // Нормализация ошибок сети/таймаута для более понятных сообщений в UI
    const status = error?.response?.status;
    const isTimeout =
      error?.code === 'ECONNABORTED' ||
      (typeof error?.message === 'string' && error.message.toLowerCase().includes('timeout'));
    const isNetwork = !error?.response;

    if (isNetwork) {
      error.message = isTimeout
        ? 'Тайм-аут запроса. Сервер может быть недоступен.'
        : 'Ошибка сети. Сервер недоступен.';
    } else if (typeof status === 'number' && status >= 500) {
      error.message = 'Ошибка сервера. Попробуйте позже.';
    }

    return Promise.reject(error);
  }
);

// API для работы с заявками
// NB: коллекционный POST идёт со слешем — Django APPEND_SLASH иначе делает 301
// на POST, а браузер на редиректе превращает POST→GET, и форма «создания»
// молча получает список тикетов вместо созданной заявки.
export const ticketsApi = {
  getAll: (params?: any) => api.get('/api/tickets/', { params }),
  getById: (id: string) => api.get(`/api/tickets/${id}`),
  create: (data: any) => api.post('/api/tickets/', data),
  updateStatus: (id: string, status: string, note?: string) => api.put(`/api/tickets/${id}/status`, { status, note }),
  updateFlags: (id: string, flags: { isGameInstall?: boolean }) => api.put(`/api/tickets/${id}/flags`, flags),
  delete: (id: string) => api.delete(`/api/tickets/${id}`),
};

// API для работы с пользователями
export const usersApi = {
  getAll: () => api.get('/api/users'),
  getById: (id: string) => api.get(`/api/users/${id}`),
  update: (id: string, data: any) => api.put(`/api/users/${id}`, data),
  delete: (id: string) => api.delete(`/api/users/${id}`),
  // getAvailableOperators removed — use /api/tickets/transfer-candidates instead.
};

// API для статистики
export const statsApi = {
  getOverview: (period?: string | { startDate: Date; endDate: Date }, clientId?: string) => {
    let params: any = {};
    
    if (typeof period === 'object') {
      params.startDate = period.startDate.toISOString();
      params.endDate = period.endDate.toISOString();
    } else if (period) {
      params.period = period;
    }
    
    if (clientId) {
      params.clientId = clientId;
    }
    
    return api.get('/api/tickets/stats/overview', { params });
  },
  getClients: (period?: string | { startDate: Date; endDate: Date }) => {
    if (typeof period === 'object') {
      const params = new URLSearchParams({
        startDate: period.startDate.toISOString(),
        endDate: period.endDate.toISOString()
      });
      return api.get(`/api/tickets/stats/clients?${params}`);
    }
    return api.get(`/api/tickets/stats/clients${period ? `?period=${period}` : ''}`);
  },
  // Per-client detail dashboard: CSAT/NPS, trend, health-score, perf.
  // Mirrors /stats/clients but scoped to one chatId and a lot richer.
  getClientDetail: (chatId: string, period?: string) => {
    const qs = period ? `?period=${period}` : '';
    return api.get(`/api/tickets/stats/clients/${encodeURIComponent(chatId)}${qs}`);
  },
  // Block/unblock a client (telegramChatId) so the bot stops reacting.
  blockClient: (chatId: string, reason?: string) =>
    api.post(`/api/tickets/clients/${encodeURIComponent(chatId)}/block`, { reason }),
  unblockClient: (chatId: string) =>
    api.post(`/api/tickets/clients/${encodeURIComponent(chatId)}/unblock`),
  getSLA: (period?: string | { startDate: Date; endDate: Date }, clientId?: string) => {
    let params: any = {};
    
    if (typeof period === 'object') {
      params.startDate = period.startDate.toISOString();
      params.endDate = period.endDate.toISOString();
    } else if (period) {
      params.period = period;
    }
    
    if (clientId) {
      params.clientId = clientId;
    }
    
    return api.get('/api/stats/sla', { params });
  },
};

// API для Telegram
export const telegramApi = {
  sendMessage: (chatId: string, message: string, ticketId?: string) =>
    api.post('/api/telegram/send-message', { chatId, message, ticketId }),
  upload: (ticketId: string, file: File | Blob, fileName?: string) => {
    const form = new FormData();
    form.append('ticketId', ticketId);
    form.append('file', file, fileName || (file as any).name || 'file');
    return api.post('/api/telegram/upload', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // Superadmin-only bot configuration (token / webhook).
  getConfig: () => api.get('/api/telegram/config'),
  updateConfig: (body: { botToken?: string; webhookUrl?: string; webhookSecret?: string }) =>
    api.put('/api/telegram/config', body),
  setWebhook: () => api.post('/api/telegram/set-webhook'),
};

// Finance module (ADMIN-ONLY). Суммы — числа (Decimal на бэке), валюта отдельно.
export const financeApi = {
  getSummary: (period?: string) =>
    api.get('/api/finance/summary', { params: period ? { period } : {} }),
  pricingAnalysis: (rate?: number, currency?: string) =>
    api.get('/api/finance/pricing-analysis', { params: { ...(rate ? { rate } : {}), ...(currency ? { currency } : {}) } }),
  // Балансы по счетам за месяц (обнуляются каждый месяц); без period — за всё время
  accountBalances: (period?: string) =>
    api.get('/api/finance/account-balances', { params: period ? { period } : {} }),
  // Переводы между счетами
  getTransfers: () => api.get('/api/finance/transfers'),
  createTransfer: (body: any) => api.post('/api/finance/transfers', body),
  deleteTransfer: (id: string) => api.delete(`/api/finance/transfers/${id}`),

  // Счета
  getAccounts: (activeOnly?: boolean) =>
    api.get('/api/finance/accounts', { params: activeOnly ? { active: '1' } : {} }),
  createAccount: (body: any) => api.post('/api/finance/accounts', body),
  updateAccount: (id: string, body: any) => api.patch(`/api/finance/accounts/${id}`, body),
  deleteAccount: (id: string) => api.delete(`/api/finance/accounts/${id}`),
  accountOperations: (id: string, period?: string) =>
    api.get(`/api/finance/accounts/${id}/operations`, { params: period ? { period } : {} }),

  // Оплаты клиентов
  getPayments: (period?: string, extra?: any) =>
    api.get('/api/finance/payments', { params: { ...(period ? { period } : {}), ...(extra || {}) } }),
  generatePayments: (period?: string) =>
    api.post('/api/finance/payments/generate', period ? { period } : {}),
  updatePayment: (id: string, body: any) => api.patch(`/api/finance/payments/${id}`, body),

  // Зарплаты (с зачётом)
  getPayroll: (period?: string, extra?: any) =>
    api.get('/api/finance/payroll', { params: { ...(period ? { period } : {}), ...(extra || {}) } }),
  generatePayroll: (period?: string) =>
    api.post('/api/finance/payroll/generate', period ? { period } : {}),
  updatePayroll: (id: string, body: any) => api.patch(`/api/finance/payroll/${id}`, body),
  deletePayroll: (id: string) => api.delete(`/api/finance/payroll/${id}`),
  // Выплата зарплаты (сумма + счёт откуда); поддерживает частичные доплаты
  payPayroll: (id: string, body: any) => api.post(`/api/finance/payroll/${id}/pay`, body),
  deletePayrollPayment: (id: string, paymentId: string) =>
    api.delete(`/api/finance/payroll/${id}/pay`, { params: { paymentId } }),

  // Прочие расходы (не зарплаты)
  getExpenses: (period?: string, extra?: any) =>
    api.get('/api/finance/expenses', { params: { ...(period ? { period } : {}), ...(extra || {}) } }),
  createExpense: (body: any) => api.post('/api/finance/expenses', body),
  generateExpenses: (period?: string) =>
    api.post('/api/finance/expenses/generate', period ? { period } : {}),
  updateExpense: (id: string, body: any) => api.patch(`/api/finance/expenses/${id}`, body),
  deleteExpense: (id: string) => api.delete(`/api/finance/expenses/${id}`),
};

// Clients billing (ADMIN-ONLY) — реальная сущность Client (тариф/валюта/статус).
export const clientsApi = {
  list: (params?: any) => api.get('/api/clients/', { params: params || {} }),
  get: (id: string) => api.get(`/api/clients/${id}`),
  byChat: (chatId: string) => api.get(`/api/clients/by-chat/${encodeURIComponent(chatId)}`),
  update: (id: string, body: any) => api.patch(`/api/clients/${id}`, body),
};

// Verification status polling during registration / login (no auth).
export const verifyStatus = (code: string) =>
  api.get('/api/auth/verify-status', { params: { code } });

export default api;